import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { ARROW_LEFT_KEY, ARROW_RIGHT_KEY, ENTER_KEY, ESCAPE_KEY } from 'common/constants/general';
import { Button } from 'components/ui/Button';
import type { ButtonProps } from 'components/ui/Button';
import { mergeSx } from 'components/ui/utilities/mergeSx';
import { useZoom } from 'context/ZoomContext';
import { BRAND, GREEN, NEUTRAL } from 'theme';
import { queryGuidedTourElement } from './GuidedTour.domUtils';
import {
  getGuidedTourScaledPx,
  GuidedTourActionGroup,
  GuidedTourActionPointer,
  GuidedTourActions,
  GuidedTourBody,
  GuidedTourCard,
  GuidedTourEyebrow,
  GuidedTourInteractionBlocker,
  GuidedTourOverlay,
  GuidedTourProgress,
  GuidedTourSpotlight,
  GuidedTourTitle,
} from './GuidedTour.styles';
import type { GuidedTourStep } from './GuidedTour.types';
import {
  getGuidedTourStepTarget,
  getNextVisibleStepIndex,
  getPreviousVisibleStepIndex,
  getVisibleTourStepIndexes,
} from './GuidedTour.utils';
import { useGuidedTour } from './useGuidedTour';

const CARD_MARGIN = 10;
const COMPACT_CARD_MARGIN = 12;
const COMPACT_VIEWPORT_HEIGHT_PX = 520;
const COMPACT_VIEWPORT_WIDTH_PX = 640;
const DEFAULT_CARD_WIDTH = 300;
const SPOTLIGHT_PADDING = 2;
const FALLBACK_CARD_SIZE = {
  height: 160,
  width: DEFAULT_CARD_WIDTH,
};
const ACTION_POINTER_OFFSET = 6;
const POINTER_CLICK_ANIMATION_MS = 620;
const POINTER_CLICK_LEAD_MS = 260;
const POST_BEFORE_STEP_RECT_REFRESH_DELAYS_MS = [0, 16, 32, 48, 64, 96, 128, 180, 240, 320, 420, 620];
const GUIDED_TOUR_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');
const GUIDED_TOUR_PRIMARY_ACTION_SELECTOR = '[data-guided-tour-primary-action="true"]';
const GUIDED_TOUR_BACK_ACTION_SELECTOR = '[data-guided-tour-back-action="true"]';
const GUIDED_TOUR_SKIP_ACTION_SELECTOR = '[data-guided-tour-skip-action="true"]';
const TAB_KEY = 'Tab';

interface TourCardSize {
  height: number;
  width: number;
}

interface ViewportSize {
  height: number;
  width: number;
}

type GuidedTourButtonVariant = 'back' | 'next' | 'skip';

const getTourButtonSx = (variant: GuidedTourButtonVariant) => {
  const baseSx = {
    borderStyle: 'solid',
    borderWidth: '1px',
    borderRadius: getGuidedTourScaledPx(6),
    boxShadow: 'none',
    fontSize: getGuidedTourScaledPx(10),
    fontWeight: 700,
    lineHeight: 1.25,
    minHeight: getGuidedTourScaledPx(26),
    minWidth: getGuidedTourScaledPx(44),
    padding: `${getGuidedTourScaledPx(4)} ${getGuidedTourScaledPx(8)}`,
    textTransform: 'none',
  } as const;

  if (variant === 'next') {
    return {
      ...baseSx,
      backgroundColor: GREEN[500],
      borderColor: GREEN[500],
      boxShadow: 'none',
      color: NEUTRAL.white,
      '&:hover': {
        backgroundColor: GREEN[600],
        borderColor: GREEN[600],
        boxShadow: 'none',
      },
    };
  }

  if (variant === 'back') {
    return {
      ...baseSx,
      backgroundColor: NEUTRAL.white,
      borderColor: BRAND[500],
      boxShadow: 'none',
      color: BRAND[600],
      '&:hover': {
        backgroundColor: BRAND[100],
        borderColor: BRAND[600],
        boxShadow: 'none',
      },
    };
  }

  return {
    ...baseSx,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    boxShadow: 'none',
    color: BRAND[600],
    '&:hover': {
      backgroundColor: BRAND[100],
      borderColor: 'transparent',
      boxShadow: 'none',
      color: BRAND[700],
    },
  };
};

type GuidedTourButtonProps = Omit<ButtonProps, 'visualVariant'> & {
  tourVariant: GuidedTourButtonVariant;
};

const GuidedTourButton = ({ tourVariant, sx, ...props }: GuidedTourButtonProps) => (
  <Button sx={mergeSx(getTourButtonSx(tourVariant), sx)} {...props} />
);

const isTargetVisible = (target: HTMLElement) => {
  const rect = target.getBoundingClientRect();

  return rect.width > 0 && rect.height > 0;
};

const hasGuidedTourActionPreview = (step: GuidedTourStep) =>
  Boolean(step.actionTarget || step.actionTargetSequence?.length);

const getGuidedTourActionPreviewDurationMs = (step: GuidedTourStep) =>
  step.actionTargetSequenceDurationMs ?? step.beforeStepDelayMs ?? 0;

const getGuidedTourActionPreviewSettleMs = (step: GuidedTourStep) =>
  step.actionTargetSequence?.length ? POINTER_CLICK_ANIMATION_MS : 0;

const getGuidedTourActionContentDelayMs = (step: GuidedTourStep) =>
  getGuidedTourActionPreviewDurationMs(step)
  + getGuidedTourActionPreviewSettleMs(step)
  + (step.contentReadyDelayMs ?? 120);

const waitForGuidedTourStepTarget = (
  step: GuidedTourStep,
  onReady: () => void,
  timeoutMs = 3000
) => {
  if (!step.target) {
    onReady();
    return () => undefined;
  }

  let frameId: number | null = null;
  let isReady = false;
  let timeoutId: number | null = null;
  const markReady = () => {
    if (isReady) {
      return;
    }

    isReady = true;
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
    if (frameId !== null) {
      window.cancelAnimationFrame(frameId);
    }
    onReady();
  };
  timeoutId = window.setTimeout(markReady, timeoutMs);
  const waitForVisibleTarget = () => {
    if (isReady) {
      return;
    }

    const target = getGuidedTourStepTarget(step);

    if (target && isTargetVisible(target)) {
      markReady();
      return;
    }

    frameId = window.requestAnimationFrame(waitForVisibleTarget);
  };

  waitForVisibleTarget();

  return () => {
    isReady = true;
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }

    if (frameId !== null) {
      window.cancelAnimationFrame(frameId);
    }
  };
};

const isRectWithinViewport = (rect: DOMRect) =>
  rect.top >= CARD_MARGIN
  && rect.left >= CARD_MARGIN
  && rect.bottom <= window.innerHeight - CARD_MARGIN
  && rect.right <= window.innerWidth - CARD_MARGIN;

const getElementLayoutRect = (target: HTMLElement) => {
  const rect = target.getBoundingClientRect();

  return new DOMRect(rect.left, rect.top, rect.width, rect.height);
};

const getVisibleElementRect = (selector: string) => {
  const target = queryGuidedTourElement(selector);

  return target && isTargetVisible(target) ? getElementLayoutRect(target) : null;
};

const getFocusableTourElements = (container: HTMLElement) =>
  Array.from(container.querySelectorAll<HTMLElement>(GUIDED_TOUR_FOCUSABLE_SELECTOR)).filter(isTargetVisible);

const getNextTourFocusIndex = (currentIndex: number, itemCount: number, isReverse: boolean) => {
  if (isReverse) {
    return currentIndex <= 0 ? itemCount - 1 : currentIndex - 1;
  }

  return currentIndex === -1 || currentIndex === itemCount - 1 ? 0 : currentIndex + 1;
};

const getCombinedRect = (rects: DOMRect[]) => {
  if (!rects.length) {
    return null;
  }

  const left = Math.floor(Math.min(...rects.map(rect => rect.left)));
  const top = Math.floor(Math.min(...rects.map(rect => rect.top)));
  const right = Math.ceil(Math.max(...rects.map(rect => rect.right)));
  const bottom = Math.ceil(Math.max(...rects.map(rect => rect.bottom)));

  return new DOMRect(left, top, right - left, bottom - top);
};

const getVisibleSpotlightRects = (
  spotlightRects: DOMRect[],
  actionTargetRect: DOMRect | null,
  isStepContentReady: boolean
) => {
  if (isStepContentReady || spotlightRects.length > 0) {
    return spotlightRects;
  }

  return actionTargetRect ? [actionTargetRect] : [];
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getViewportSize = (): ViewportSize => ({
  height: typeof window === 'undefined' ? 0 : window.visualViewport?.height ?? window.innerHeight,
  width: typeof window === 'undefined' ? 0 : window.visualViewport?.width ?? window.innerWidth,
});

const isCompactGuidedTourViewport = ({ height, width }: ViewportSize) =>
  width <= COMPACT_VIEWPORT_WIDTH_PX || height <= COMPACT_VIEWPORT_HEIGHT_PX;

const getSpotlightStyle = (rect: DOMRect, viewportSize: ViewportSize, padding = SPOTLIGHT_PADDING) => {
  const left = clamp(rect.left - padding, 0, viewportSize.width);
  const top = clamp(rect.top - padding, 0, viewportSize.height);
  const right = clamp(rect.right + padding, 0, viewportSize.width);
  const bottom = clamp(rect.bottom + padding, 0, viewportSize.height);

  return {
    height: Math.max(bottom - top, 0),
    left,
    top,
    width: Math.max(right - left, 0),
  };
};

const getRectOverlapArea = (
  first: { bottom: number; left: number; right: number; top: number },
  second: { bottom: number; left: number; right: number; top: number }
) => {
  const width = Math.max(0, Math.min(first.right, second.right) - Math.max(first.left, second.left));
  const height = Math.max(0, Math.min(first.bottom, second.bottom) - Math.max(first.top, second.top));

  return width * height;
};

type GuidedTourResolvedPlacement = 'bottom' | 'left' | 'right' | 'top';

const getCardCandidate = (
  placement: GuidedTourResolvedPlacement,
  targetRect: DOMRect,
  cardSize: TourCardSize
) => {
  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;

  switch (placement) {
    case 'top':
      return {
        left: targetCenterX - cardSize.width / 2,
        top: targetRect.top - cardSize.height - CARD_MARGIN,
      };
    case 'right':
      return {
        left: targetRect.right + CARD_MARGIN,
        top: targetCenterY - cardSize.height / 2,
      };
    case 'left':
      return {
        left: targetRect.left - cardSize.width - CARD_MARGIN,
        top: targetCenterY - cardSize.height / 2,
      };
    case 'bottom':
    default:
      return {
        left: targetCenterX - cardSize.width / 2,
        top: targetRect.bottom + CARD_MARGIN,
      };
  }
};

const getPreferredPlacements = (preferredPlacement?: string) => {
  const placements: GuidedTourResolvedPlacement[] = ['bottom', 'right', 'left', 'top'];

  if (!preferredPlacement || !placements.includes(preferredPlacement as GuidedTourResolvedPlacement)) {
    return placements;
  }

  return [
    preferredPlacement as GuidedTourResolvedPlacement,
    ...placements.filter(placement => placement !== preferredPlacement),
  ];
};

const getPlacementFitScore = (
  placement: GuidedTourResolvedPlacement,
  targetRect: DOMRect,
  cardSize: TourCardSize
) => {
  const rawPosition = getCardCandidate(placement, targetRect, cardSize);
  const maxLeft = Math.max(window.innerWidth - cardSize.width - CARD_MARGIN, CARD_MARGIN);
  const maxTop = Math.max(window.innerHeight - cardSize.height - CARD_MARGIN, CARD_MARGIN);
  const left = clamp(rawPosition.left, CARD_MARGIN, maxLeft);
  const top = clamp(rawPosition.top, CARD_MARGIN, maxTop);

  return Math.abs(rawPosition.left - left) + Math.abs(rawPosition.top - top);
};

const getCardPosition = (
  targetRect: DOMRect | null,
  cardSize: TourCardSize,
  isCompactViewport: boolean,
  preferredPlacement?: string
) => {
  if (isCompactViewport) {
    return {
      bottom: COMPACT_CARD_MARGIN,
      left: COMPACT_CARD_MARGIN,
      right: COMPACT_CARD_MARGIN,
      top: 'auto',
      transform: 'none',
    };
  }

  if (!targetRect) {
    return {
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
    };
  }

  const maxLeft = Math.max(window.innerWidth - cardSize.width - CARD_MARGIN, CARD_MARGIN);
  const maxTop = Math.max(window.innerHeight - cardSize.height - CARD_MARGIN, CARD_MARGIN);
  const targetBounds = {
    bottom: targetRect.bottom,
    left: targetRect.left,
    right: targetRect.right,
    top: targetRect.top,
  };
  const preferredPlacements = getPreferredPlacements(preferredPlacement);
  const firstPlacement = preferredPlacements[0];
  const firstPlacementFitScore = getPlacementFitScore(firstPlacement, targetRect, cardSize);
  const placementCandidates = firstPlacementFitScore < 80 ? [
    firstPlacement,
    ...preferredPlacements.filter(placement => placement !== firstPlacement),
  ] : preferredPlacements;
  const candidates = placementCandidates.map((placement, index) => {
    const rawPosition = getCardCandidate(placement, targetRect, cardSize);
    const left = clamp(rawPosition.left, CARD_MARGIN, maxLeft);
    const top = clamp(rawPosition.top, CARD_MARGIN, maxTop);
    const cardBounds = {
      bottom: top + cardSize.height,
      left,
      right: left + cardSize.width,
      top,
    };
    const cardCenterX = left + cardSize.width / 2;
    const cardCenterY = top + cardSize.height / 2;
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const targetCenterY = targetRect.top + targetRect.height / 2;
    const offscreenPenalty =
      Math.abs(rawPosition.left - left)
      + Math.abs(rawPosition.top - top);
    const overlapPenalty = getRectOverlapArea(cardBounds, targetBounds);
    const distancePenalty = Math.hypot(cardCenterX - targetCenterX, cardCenterY - targetCenterY) * 0.12;

    return {
      left,
      placement,
      score: overlapPenalty * 8 + offscreenPenalty * 2 + distancePenalty + index * 28,
      top,
    };
  });
  const bestCandidate = candidates.sort((a, b) => a.score - b.score)[0];

  return {
    left: bestCandidate.left,
    top: bestCandidate.top,
    transform: 'none',
  };
};

const GuidedTour = () => {
  const { zoomLevel } = useZoom();
  const {
    activeStepIndex,
    activeTour,
    dismissTour,
    goToNextStep,
    goToPreviousStep,
    status,
  } = useGuidedTour();
  const cardRef = useRef<HTMLElement | null>(null);
  const [actionTargetRect, setActionTargetRect] = useState<DOMRect | null>(null);
  const [cardSize, setCardSize] = useState<TourCardSize>(FALLBACK_CARD_SIZE);
  const [visibleActionTargetSelector, setVisibleActionTargetSelector] = useState<string | null>(null);
  const [isPointerClicking, setIsPointerClicking] = useState(false);
  const [isStepContentReady, setIsStepContentReady] = useState(true);
  const [spotlightRects, setSpotlightRects] = useState<DOMRect[]>([]);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [viewportSize, setViewportSize] = useState<ViewportSize>(() => getViewportSize());
  const activeStep = activeTour?.steps[activeStepIndex];
  const actionTargetSelector = visibleActionTargetSelector ?? activeStep?.actionTarget ?? null;
  const isCompactViewport = isCompactGuidedTourViewport(viewportSize);

  const focusPrimaryTourAction = useCallback(() => {
    const primaryAction = cardRef.current?.querySelector<HTMLElement>(GUIDED_TOUR_PRIMARY_ACTION_SELECTOR);

    if (primaryAction && isTargetVisible(primaryAction)) {
      primaryAction.focus({ preventScroll: true });
      return;
    }

    cardRef.current?.focus({ preventScroll: true });
  }, []);

  const triggerTourAction = useCallback((selector: string) => {
    const action = cardRef.current?.querySelector<HTMLElement>(selector);

    if (!action || !isTargetVisible(action)) {
      return;
    }

    action.focus({ preventScroll: true });
    action.click();
  }, []);

  const triggerBackTourAction = useCallback(() => {
    triggerTourAction(GUIDED_TOUR_BACK_ACTION_SELECTOR);
  }, [triggerTourAction]);

  const triggerPrimaryTourAction = useCallback(() => {
    triggerTourAction(GUIDED_TOUR_PRIMARY_ACTION_SELECTOR);
  }, [triggerTourAction]);

  const triggerSkipTourAction = useCallback(() => {
    triggerTourAction(GUIDED_TOUR_SKIP_ACTION_SELECTOR);
  }, [triggerTourAction]);

  const trapTourFocus = useCallback((event: KeyboardEvent) => {
    const card = cardRef.current;

    if (!card) {
      event.preventDefault();
      return;
    }

    const focusableElements = getFocusableTourElements(card);

    if (!focusableElements.length) {
      event.preventDefault();
      card.focus({ preventScroll: true });
      return;
    }

    const activeElement = document.activeElement as HTMLElement | null;
    const currentIndex = activeElement ? focusableElements.indexOf(activeElement) : -1;
    const nextIndex = getNextTourFocusIndex(currentIndex, focusableElements.length, event.shiftKey);

    event.preventDefault();
    event.stopPropagation();
    focusableElements[nextIndex].focus({ preventScroll: true });
  }, []);

  const blockAppInteraction = useCallback((event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    focusPrimaryTourAction();
  }, [focusPrimaryTourAction]);

  const handleCardKeyDown = useCallback((event: ReactKeyboardEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  // Keep keyboard control inside the active tour, even though the card is rendered through a body portal.
  useEffect(() => {
    if (!activeStep || status !== 'running') {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === TAB_KEY) {
        trapTourFocus(event);
        return;
      }

      if (event.key === ESCAPE_KEY) {
        event.preventDefault();
        event.stopPropagation();
        triggerSkipTourAction();
        return;
      }

      if (event.key === ARROW_LEFT_KEY) {
        event.preventDefault();
        event.stopPropagation();
        triggerBackTourAction();
        return;
      }

      if (event.key === ARROW_RIGHT_KEY) {
        event.preventDefault();
        event.stopPropagation();
        triggerPrimaryTourAction();
        return;
      }

      if (event.key === ENTER_KEY) {
        event.preventDefault();
        event.stopPropagation();
        triggerPrimaryTourAction();
        return;
      }

      if (!cardRef.current?.contains(event.target as Node | null)) {
        event.preventDefault();
        event.stopPropagation();
        focusPrimaryTourAction();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [
    activeStep,
    focusPrimaryTourAction,
    status,
    trapTourFocus,
    triggerBackTourAction,
    triggerPrimaryTourAction,
    triggerSkipTourAction,
  ]);

  // Do not steal focus from controls the tour just opened, such as collapsible search or menus.
  useEffect(() => {
    if (
      !activeStep
      || status !== 'running'
      || !isStepContentReady
      || hasGuidedTourActionPreview(activeStep)
    ) {
      return;
    }

    focusPrimaryTourAction();
  }, [activeStep, focusPrimaryTourAction, isStepContentReady, status]);

  // Animate pointer previews in sync with steps that programmatically open one or more menus.
  useEffect(() => {
    if (!activeStep || status !== 'running') {
      setVisibleActionTargetSelector(null);
      setIsPointerClicking(false);
      return undefined;
    }

    const timers: number[] = [];
    let actionTargets: string[] = [];

    if (activeStep.actionTargetSequence?.length) {
      actionTargets = activeStep.actionTargetSequence;
    } else if (activeStep.actionTarget) {
      actionTargets = [activeStep.actionTarget];
    }

    const sequenceDurationMs = getGuidedTourActionPreviewDurationMs(activeStep);

    if (!sequenceDurationMs || actionTargets.length === 0) {
      setVisibleActionTargetSelector(null);
      setIsPointerClicking(false);
      return undefined;
    }

    const stepDelay = sequenceDurationMs / actionTargets.length;

    actionTargets.forEach((selector, index) => {
      const targetStartMs = stepDelay * index;
      const clickStartMs = Math.max(targetStartMs + stepDelay - POINTER_CLICK_LEAD_MS, targetStartMs);

      timers.push(window.setTimeout(() => {
        setVisibleActionTargetSelector(selector);
      }, targetStartMs));
      timers.push(window.setTimeout(() => {
        setIsPointerClicking(true);
      }, clickStartMs));
      timers.push(window.setTimeout(() => {
        setIsPointerClicking(false);
      }, targetStartMs + stepDelay + POINTER_CLICK_ANIMATION_MS));
    });

    timers.push(window.setTimeout(() => {
      setVisibleActionTargetSelector(actionTargets[actionTargets.length - 1]);
      setIsPointerClicking(false);
    }, sequenceDurationMs + POINTER_CLICK_ANIMATION_MS));

    return () => {
      timers.forEach(timerId => window.clearTimeout(timerId));
      setVisibleActionTargetSelector(null);
      setIsPointerClicking(false);
    };
  }, [activeStep, status]);

  // Delay the card/highlight until guided pointer clicks have had time to open the target surface.
  useEffect(() => {
    if (!activeStep || status !== 'running') {
      setIsStepContentReady(true);
      return undefined;
    }

    if (activeStep.waitForTargetBeforeShowing) {
      setIsStepContentReady(false);
      return waitForGuidedTourStepTarget(
        activeStep,
        () => setIsStepContentReady(true),
        activeStep.waitForTargetTimeoutMs
      );
    }

    const actionPreviewDurationMs = getGuidedTourActionPreviewDurationMs(activeStep);
    const shouldDelayContent = Boolean(actionPreviewDurationMs && hasGuidedTourActionPreview(activeStep));

    if (!shouldDelayContent) {
      setIsStepContentReady(true);
      return undefined;
    }

    setIsStepContentReady(false);
    let cleanupTargetWait: (() => void) | null = null;
    const timerId = window.setTimeout(() => {
      cleanupTargetWait = waitForGuidedTourStepTarget(
        activeStep,
        () => setIsStepContentReady(true),
        activeStep.waitForTargetTimeoutMs ?? 1000
      );
    }, getGuidedTourActionContentDelayMs(activeStep));

    return () => {
      window.clearTimeout(timerId);
      cleanupTargetWait?.();
    };
  }, [activeStep, status]);

  // Clear stale geometry immediately when changing steps so Back/Next never shows the previous target.
  useEffect(() => {
    setActionTargetRect(null);
    setSpotlightRects([]);
    setTargetRect(null);
  }, [activeStep?.id]);

  // Track viewport changes separately so compact positioning responds to browser zoom, resize, and mobile viewports.
  useEffect(() => {
    if (!activeStep || status !== 'running') {
      return undefined;
    }

    const updateViewportSize = () => {
      setViewportSize(getViewportSize());
    };

    updateViewportSize();
    window.addEventListener('resize', updateViewportSize);
    window.visualViewport?.addEventListener('resize', updateViewportSize);
    window.visualViewport?.addEventListener('scroll', updateViewportSize);

    return () => {
      window.removeEventListener('resize', updateViewportSize);
      window.visualViewport?.removeEventListener('resize', updateViewportSize);
      window.visualViewport?.removeEventListener('scroll', updateViewportSize);
    };
  }, [activeStep, status]);

  // Measure after layout so the spotlight and card follow opened menus, scrolling, and resized targets.
  useLayoutEffect(() => {
    if (!activeStep || status !== 'running') {
      return undefined;
    }

    let frameId: number | null = null;
    let trackingFrameId: number | null = null;
    const delayedFrameTimerIds: number[] = [];

    const updateRects = () => {
      const target = getGuidedTourStepTarget(activeStep);
      const actionTarget = actionTargetSelector
        ? queryGuidedTourElement(actionTargetSelector)
        : null;
      const nextActionTargetRect = actionTarget && isTargetVisible(actionTarget)
        ? getElementLayoutRect(actionTarget)
        : null;
      const targetLayoutRect = target && isTargetVisible(target)
        ? getElementLayoutRect(target)
        : null;
      const highlightTargetRects = activeStep.highlightTargets
        ?.map(getVisibleElementRect)
        .filter((rect): rect is DOMRect => Boolean(rect)) ?? [];
      const fallbackAnchorRect = getCombinedRect(highlightTargetRects);

      if (target && targetLayoutRect) {
        if (!isRectWithinViewport(targetLayoutRect)) {
          target.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
        }
      } else {
        setTargetRect(null);
      }

      frameId = window.requestAnimationFrame(() => {
        let nextSpotlightRects: DOMRect[] = [];

        if (highlightTargetRects.length) {
          nextSpotlightRects = highlightTargetRects;
        } else if (targetLayoutRect) {
          nextSpotlightRects = [targetLayoutRect];
        } else if (nextActionTargetRect) {
          nextSpotlightRects = [nextActionTargetRect];
        }

        setTargetRect(targetLayoutRect ?? fallbackAnchorRect);
        setSpotlightRects(nextSpotlightRects);
        setActionTargetRect(nextActionTargetRect);
      });
    };

    updateRects();
    const rectRefreshStartMs = getGuidedTourActionPreviewDurationMs(activeStep);
    const startTargetTracking = () => {
      if (!activeStep.targetTrackingDurationMs) {
        return;
      }

      const trackingEndTime = window.performance.now() + activeStep.targetTrackingDurationMs;
      const trackTargetRect = () => {
        updateRects();

        if (window.performance.now() < trackingEndTime) {
          trackingFrameId = window.requestAnimationFrame(trackTargetRect);
        }
      };

      trackTargetRect();
    };

    if (rectRefreshStartMs) {
      POST_BEFORE_STEP_RECT_REFRESH_DELAYS_MS.forEach(delayMs => {
        delayedFrameTimerIds.push(window.setTimeout(updateRects, rectRefreshStartMs + delayMs));
      });
      delayedFrameTimerIds.push(window.setTimeout(startTargetTracking, rectRefreshStartMs));
    } else {
      startTargetTracking();
    }

    window.addEventListener('resize', updateRects);
    window.visualViewport?.addEventListener('resize', updateRects);
    window.visualViewport?.addEventListener('scroll', updateRects);
    window.addEventListener('scroll', updateRects, true);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      if (trackingFrameId !== null) {
        window.cancelAnimationFrame(trackingFrameId);
      }

      delayedFrameTimerIds.forEach(timerId => window.clearTimeout(timerId));

      window.removeEventListener('resize', updateRects);
      window.visualViewport?.removeEventListener('resize', updateRects);
      window.visualViewport?.removeEventListener('scroll', updateRects);
      window.removeEventListener('scroll', updateRects, true);
    };
  }, [actionTargetSelector, activeStep, status]);

  // Track card size changes so placement can avoid covering the highlighted target.
  useLayoutEffect(() => {
    if (!activeStep || status !== 'running' || !cardRef.current) {
      return undefined;
    }

    const updateCardSize = () => {
      const rect = cardRef.current?.getBoundingClientRect();

      if (!rect) {
        return;
      }

      setCardSize({
        height: rect.height,
        width: rect.width,
      });
    };
    updateCardSize();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(updateCardSize);
    resizeObserver.observe(cardRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [activeStep, status]);

  const anchorRect = targetRect ?? actionTargetRect;
  const visibleSpotlightRects = getVisibleSpotlightRects(spotlightRects, actionTargetRect, isStepContentReady);
  const backdropSpotlightRect = visibleSpotlightRects.length > 1
    ? getCombinedRect(visibleSpotlightRects)
    : visibleSpotlightRects[0];
  const cardPosition = useMemo(
    () => getCardPosition(anchorRect, cardSize, isCompactViewport, activeStep?.placement),
    [activeStep?.placement, anchorRect, cardSize, isCompactViewport]
  );

  if (!activeTour || !activeStep || status !== 'running' || typeof document === 'undefined') {
    return null;
  }

  const visibleStepIndexes = getVisibleTourStepIndexes(activeTour);
  const stepCount = visibleStepIndexes.length;
  const visibleStepNumber = Math.max(visibleStepIndexes.indexOf(activeStepIndex) + 1, 1);
  const previousVisibleStepIndex = getPreviousVisibleStepIndex(activeTour, activeStepIndex);
  const isLastVisibleStep = getNextVisibleStepIndex(activeTour, activeStepIndex) === -1;

  return createPortal(
    <>
      <GuidedTourOverlay aria-hidden hasSpotlight={visibleSpotlightRects.length > 0} />
      <GuidedTourInteractionBlocker
        aria-hidden
        onClick={blockAppInteraction}
        onContextMenu={blockAppInteraction}
        onDoubleClick={blockAppInteraction}
        onMouseDown={blockAppInteraction}
        onPointerDown={blockAppInteraction}
        onTouchMove={blockAppInteraction}
        onWheel={blockAppInteraction}
      />
      {backdropSpotlightRect && (
        <GuidedTourSpotlight
          key={`${activeStep.id}-backdrop`}
          hasBackdrop
          isTrackingTarget={Boolean(activeStep.targetTrackingDurationMs)}
          style={getSpotlightStyle(backdropSpotlightRect, viewportSize, activeStep.spotlightPadding)}
        />
      )}
      {actionTargetRect && (
        <GuidedTourActionPointer
          aria-hidden
          isClicking={isPointerClicking}
          style={{
            left: actionTargetRect.left + ACTION_POINTER_OFFSET,
            top: actionTargetRect.top + ACTION_POINTER_OFFSET,
          }}
        />
      )}
      <GuidedTourCard
        ref={cardRef}
        aria-label={`${activeTour.title}: ${activeStep.title}`}
        aria-modal="true"
        onKeyDown={handleCardKeyDown}
        role="dialog"
        style={{
          ...cardPosition,
          opacity: isStepContentReady ? 1 : 0,
          pointerEvents: isStepContentReady ? undefined : 'none',
        }}
        tabIndex={-1}
        zoomLevel={zoomLevel}>
        <GuidedTourProgress>
          {visibleStepNumber}/{stepCount}
        </GuidedTourProgress>
        <GuidedTourEyebrow>{activeTour.title}</GuidedTourEyebrow>
        <GuidedTourTitle>{activeStep.title}</GuidedTourTitle>
        <GuidedTourBody>{activeStep.body}</GuidedTourBody>
        <GuidedTourActions>
          <GuidedTourButton
            aria-keyshortcuts="Escape"
            data-guided-tour-skip-action="true"
            onClick={dismissTour}
            tourVariant="skip">
            Skip
          </GuidedTourButton>
          <GuidedTourActionGroup>
            {previousVisibleStepIndex !== -1 && (
              <GuidedTourButton
                aria-keyshortcuts="ArrowLeft"
                data-guided-tour-back-action="true"
                onClick={goToPreviousStep}
                tourVariant="back">
                Back
              </GuidedTourButton>
            )}
            <GuidedTourButton
              aria-keyshortcuts="Enter ArrowRight"
              data-guided-tour-primary-action="true"
              onClick={goToNextStep}
              tourVariant="next">
              {isLastVisibleStep ? 'Done' : 'Next'}
            </GuidedTourButton>
          </GuidedTourActionGroup>
        </GuidedTourActions>
      </GuidedTourCard>
    </>,
    document.body
  );
};

export default GuidedTour;
